// Admin Stripe Section - Stripe transaction analytics dashboard
// Sub-admin page for viewing Stripe transaction data with line chart and summary stats

import React, { useState } from 'react';
import { MainLayout } from '../../../../components/Layout';
import { LoadingPanel } from '../../../../components';
import { AdminPageHeader } from '../../components';
import { AdminStripeManager } from './managers/admin-stripe-manager';
import { useManager } from '../../../../core';
import { StripeTransactionChart, StripeSummaryStats } from './components';
import './AdminStripeSection.css';

export function AdminStripeSection() {
  const { manager, isInitialized } = useManager(AdminStripeManager);
  const [chartView, setChartView] = useState('all');
  const [amountMode, setAmountMode] = useState('actual');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [useMockData, setUseMockData] = useState(false); // TEMP: remove when no longer needed

  const handleMockToggle = () => {
    const next = !useMockData;
    setUseMockData(next);
    manager.setUseMockData(next);
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await manager.refreshTransactions();
    } finally {
      setIsRefreshing(false);
    }
  };

  const opts = { chartView, amountMode };

  if (!isInitialized) {
    return (
      <MainLayout contentClassName="main-content--admin">
        <div className="loading-container">
          <LoadingPanel
            title="Loading Stripe Analytics"
            subtitle="Setting up transaction dashboard..."
            showAppName={false}
          />
        </div>
      </MainLayout>
    );
  }

  const chartData = manager.getChartData(opts);
  const summaryStats = manager.getSummaryStats(opts);

  return (
    <MainLayout contentClassName="main-content--admin">
      <AdminPageHeader />

      <div className="admin-stripe-container">
        <div className="admin-section-content">
          <div className="admin-section-header">
            <h2>Stripe Analytics</h2>
            <p>Transaction revenue over time and summary statistics.</p>
          </div>

          {/* Chart view toggle */}
          <div className="stripe-chart-controls">
            <div className="stripe-chart-toggle-group">
              <span className="stripe-chart-toggle-label">Group by:</span>
              <div className="stripe-chart-toggle">
                <button
                  type="button"
                  className={`stripe-toggle-btn ${chartView === 'all' ? 'active' : ''}`}
                  onClick={() => setChartView('all')}
                >
                  All
                </button>
                <button
                  type="button"
                  className={`stripe-toggle-btn ${chartView === 'byPlan' ? 'active' : ''}`}
                  onClick={() => setChartView('byPlan')}
                >
                  By Plan
                </button>
                <button
                  type="button"
                  className={`stripe-toggle-btn ${chartView === 'byTier' ? 'active' : ''}`}
                  onClick={() => setChartView('byTier')}
                >
                  By Tier
                </button>
              </div>
            </div>
            <div className="stripe-chart-toggle-group">
              <span className="stripe-chart-toggle-label">Amount:</span>
              <div className="stripe-chart-toggle">
                <button
                  type="button"
                  className={`stripe-toggle-btn ${amountMode === 'actual' ? 'active' : ''}`}
                  onClick={() => setAmountMode('actual')}
                >
                  Actual Paid
                </button>
                <button
                  type="button"
                  className={`stripe-toggle-btn ${amountMode === 'full' ? 'active' : ''}`}
                  onClick={() => setAmountMode('full')}
                >
                  Full (Pre-Discount)
                </button>
              </div>
            </div>
            {/* TEMP: Mock data toggle - remove when no longer needed */}
            <button
              type="button"
              className={`stripe-toggle-btn ${useMockData ? 'active' : ''}`}
              onClick={handleMockToggle}
              title="Show 100 mock transactions for testing"
            >
              {useMockData ? 'Mock Data On' : 'Mock Data Off'}
            </button>
            <button
              type="button"
              className="stripe-refresh-btn"
              onClick={handleRefresh}
              disabled={isRefreshing}
            >
              {isRefreshing ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>

          {/* Line chart */}
          <div className="stripe-chart-section">
            <StripeTransactionChart
              chartData={chartData}
              chartView={chartView}
              getPlanColor={(id) => manager.getGroupColor(id)}
            />
          </div>

          {/* Summary stats below */}
          <div className="stripe-summary-section">
            <StripeSummaryStats
              stats={summaryStats}
              chartView={chartView}
              getPlanColor={(id) => manager.getGroupColor(id)}
            />
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
